/**
 * The single home for the entitlement wire format: `resource:action[:resourceId]`.
 * The policy editor and the permissions store both read and write grants through these
 * helpers, so the format keeps one definition.
 */

export interface EntitlementKeyParts {
	resource: string;
	action: string;
	resourceId?: string;
}

function encodeResourceAction(resource: string, action: string): string {
	return `${resource}:${action}`;
}

/** Build a grant key. The sole writer of the wire format. */
export function encodeEntitlementKey({ resource, action, resourceId }: EntitlementKeyParts): string {
	const base = encodeResourceAction(resource, action);
	return resourceId === undefined ? base : `${base}:${resourceId}`;
}

/**
 * Parse a grant key leniently: never throws; missing segments come back as `''`/`undefined`.
 * The first two segments are resource and action; everything after the second `:` is the
 * resourceId verbatim, so a colon-bearing id (`a:b`, an item primary key) survives the
 * round-trip. A key with only two segments has no resourceId (`undefined`).
 */
export function decodeEntitlementKey(key: string): EntitlementKeyParts {
	const [resource = '', action = '', ...rest] = key.split(':');
	return rest.length === 0 ? { resource, action } : { resource, action, resourceId: rest.join(':') };
}

/**
 * Whether a grant key belongs to a given `(resource, action)` row. Delimiter-anchored, so
 * action `read` does not match a `read_only` grant and resource `users` does not match a
 * `users_extended` grant (a bare `startsWith` over-grants both).
 */
export function entitlementKeyMatchesAction(
	key: string,
	{ resource, action }: Pick<EntitlementKeyParts, 'resource' | 'action'>,
): boolean {
	const prefix = encodeResourceAction(resource, action);
	return key === prefix || key.startsWith(`${prefix}:`);
}

/**
 * Drop every key equal to the encoded `parts`. Exact match (not prefix), so removing `:123`
 * keeps `:1234`.
 */
export function removeEntitlementKey(keys: string[], parts: EntitlementKeyParts): string[] {
	const exact = encodeEntitlementKey(parts);
	return keys.filter((key) => key !== exact);
}

export interface GrantIndex {
	keys: Set<string>;
	resourceActions: Set<string>;
}

/**
 * Index a user's merged grant set for O(1) `isGranted` lookups: every grant key, plus the
 * `resource:action` of every id-scoped grant (so an "any id" query can be answered).
 */
export function buildGrantIndex(grants: Iterable<string>): GrantIndex {
	const keys = new Set<string>();
	const resourceActions = new Set<string>();

	for (const grant of grants) {
		keys.add(grant);

		const { resource, action, resourceId } = decodeEntitlementKey(grant);

		if (resource && action && resourceId !== undefined) {
			resourceActions.add(encodeResourceAction(resource, action));
		}
	}

	return { keys, resourceActions };
}

/**
 * Whether the grant set covers a request. Precedence: `resource:*` blanket → for a simple
 * request the exact `resource:action`; for an id request the exact id or the `:*` action
 * wildcard; for an any-id request (`resourceId` `undefined`/`'*'`) the `:*` wildcard or any
 * id-scoped grant on that action. A simple grant never satisfies an any-id request. Admin
 * bypass is the caller's concern.
 */
export function isGranted(index: GrantIndex, request: EntitlementKeyParts): boolean {
	const { resource, action } = request;
	const resourceAction = encodeResourceAction(resource, action);
	const actionWildcard = `${resourceAction}:*`;

	if (index.keys.has(encodeResourceAction(resource, '*'))) return true;

	if (!('resourceId' in request)) {
		return index.keys.has(resourceAction);
	}

	const { resourceId } = request;

	if (resourceId === undefined || resourceId === '*') {
		return index.keys.has(actionWildcard) || index.resourceActions.has(resourceAction);
	}

	return index.keys.has(`${resourceAction}:${resourceId}`) || index.keys.has(actionWildcard);
}

/**
 * The ids a grant set allows for a scoped `resource:action`. Returns `'*'` when a `resource:*`
 * blanket or a `resource:action:*` wildcard grants every id; otherwise the specific granted ids
 * (empty when none). Use to scope a picker to the assignable items. Admin is the caller's concern.
 */
export function allowedResourceIds(index: GrantIndex, resource: string, action: string): string[] | '*' {
	const resourceAction = encodeResourceAction(resource, action);

	if (index.keys.has(encodeResourceAction(resource, '*')) || index.keys.has(`${resourceAction}:*`)) return '*';

	const prefix = `${resourceAction}:`;
	const ids: string[] = [];

	for (const key of index.keys) {
		if (key.startsWith(prefix)) ids.push(key.slice(prefix.length));
	}

	return ids;
}
